import { tokenRefreshManager } from '@/main'
import { handleTokenRefreshFailure } from './authUtils'

let refreshTimer: number | null = null
const CHECK_INTERVAL = 60 * 1000 // 每分钟检查一次（60秒）

/**
 * 启动token定时检查
 * 定期检查token是否即将过期，如果即将过期则主动刷新
 */
export function startTokenRefreshTimer(): void {
  // 清除现有的定时器
  stopTokenRefreshTimer()
  
  // 立即检查一次
  checkAndRefreshToken()
  
  // 设置定时检查
  refreshTimer = window.setInterval(() => {
    checkAndRefreshToken()
  }, CHECK_INTERVAL)
  
  console.log('✅ Token定时刷新检查已启动')
}

/**
 * 停止token定时检查
 */
export function stopTokenRefreshTimer(): void {
  if (refreshTimer !== null) {
    clearInterval(refreshTimer)
    refreshTimer = null
    console.log('⏹️ Token定时刷新检查已停止')
  }
}

/**
 * 检查并刷新token
 */
async function checkAndRefreshToken(): Promise<void> {
  try {
    const token = localStorage.getItem('apiToken')
    const refreshToken = localStorage.getItem('apiToken_refresh')
    
    // 如果没有token或refreshToken，不需要检查
    if (!token || !refreshToken) {
      return
    }
    
    // 如果正在刷新中，跳过本次检查
    if (tokenRefreshManager.isRefreshing()) {
      return
    }
    
    // 动态导入 isTokenExpired 函数，避免循环依赖
    const { isTokenExpired } = await import('./authUtils')
    
    // 检查token是否即将过期（提前5分钟）
    if (isTokenExpired(token)) {
      console.log('🔄 检测到token即将过期，开始自动刷新...')
      
      try {
        // 如果正在刷新，等待现有刷新完成
        if (tokenRefreshManager.isRefreshing() && tokenRefreshManager.getRefreshPromise()) {
          await tokenRefreshManager.getRefreshPromise()
        } else {
          // 执行刷新
          await performTokenRefresh(refreshToken)
        }
        console.log('✅ Token自动刷新成功')
      } catch (error) {
        console.error('❌ Token自动刷新失败:', error)
      }
    }
  } catch (error) {
    console.error('❌ Token检查过程中出错:', error)
  }
}

/**
 * 执行token刷新
 */
async function performTokenRefresh(refreshToken: string): Promise<string | null> {
  // 如果正在刷新，返回现有的promise
  if (tokenRefreshManager.isRefreshing() && tokenRefreshManager.getRefreshPromise()) {
    return tokenRefreshManager.getRefreshPromise()
  }
  
  // 设置刷新状态
  tokenRefreshManager.setRefreshing(true)
  
  try {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || ''
    const apiUrl = apiBaseUrl.endsWith('/') 
      ? `${apiBaseUrl}api/auth/refresh`
      : `${apiBaseUrl}/api/auth/refresh`
    
    // 获取当前品牌ID
    let currentBrandId: number | null = null
    try {
      const currentBrand = localStorage.getItem('currentBrand')
      if (currentBrand) {
        const brandData = JSON.parse(currentBrand)
        currentBrandId = brandData.id || null
      }
    } catch (error) {
      console.warn('获取当前品牌ID失败:', error)
    }
    
    const refreshPromise = fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        refreshToken,
        brandId: currentBrandId 
      })
    }).then(async (response) => {
      const data = await response.json()
      
      if (data.success && data.data) {
        const newAccessToken = data.data
        localStorage.setItem('apiToken', newAccessToken)
        return newAccessToken
      } else {
        // 刷新失败时清除所有认证状态（停止定时器）并跳转到登录页
        console.warn('❌ Token刷新失败:', data.message || '刷新令牌无效或已过期')
        // 延迟跳转，避免在定时器中立即跳转导致问题
        setTimeout(() => {
          handleTokenRefreshFailure('定时刷新器', window.location.pathname)
        }, 100)
        return null
      }
    }).catch((error) => {
      console.error('❌ Token刷新过程中出错:', error)
      // 刷新过程中出错时清除所有认证状态（停止定时器）并跳转到登录页
      import('./authUtils').then(({ handleTokenRefreshFailure }) => {
        // 延迟跳转，避免在定时器中立即跳转导致问题
        setTimeout(() => {
          handleTokenRefreshFailure('定时刷新器', window.location.pathname)
        }, 100)
      }).catch(() => {
        // 如果导入失败，至少清除localStorage并跳转
        localStorage.removeItem('apiToken')
        localStorage.removeItem('apiToken_refresh')
        localStorage.removeItem('currentUser')
        localStorage.removeItem('currentBrand')
        setTimeout(() => {
          window.location.href = '/login'
        }, 100)
      })
      return null
    })
    
    // 保存promise供其他请求复用
    tokenRefreshManager.setRefreshPromise(refreshPromise)
    
    const result = await refreshPromise
    return result
  } finally {
    // 刷新完成，重置状态
    tokenRefreshManager.setRefreshing(false)
    tokenRefreshManager.setRefreshPromise(null)
  }
}

